import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const ax  = axios.create({ baseURL: API, withCredentials: true });

// Échappement HTML — prévient XSS dans la génération PDF
function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Validation URL — interdit javascript: et data: URIs
function safeUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    return ['http:', 'https:'].includes(u.protocol) ? url : null;
  } catch { return null; }
}

/* ─── Mapping vulnérabilité → chemin visuel + lien direct GBP ───
   path  : étapes cliquables dans l'interface Google Business Profile
   link  : URL directe pour corriger (ou null si non disponible)
   type  : 'review' | 'gbp' | 'maps' — détermine la couleur et l'icône
*/
function getFixLocation(issueLabel, placeId) {
  const l = issueLabel.toLowerCase();

  if (l.includes('avis') && (l.includes('aucun') || l.includes('seulement') || l.includes('avis'))) return {
    type:  'review',
    path:  ['Google Business', 'Avis', 'Obtenir plus d\'avis'],
    reviewLink: placeId ? `https://search.google.com/local/writereview?placeid=${placeId}` : null,
    link:  'https://business.google.com/reviews',
    label: 'Gérer les avis',
  };

  if (l.includes('note')) return {
    type:  'maps',
    path:  ['Google Maps', 'Votre fiche', 'Voir et répondre aux avis'],
    link:  placeId ? `https://www.google.com/maps/place/?q=place_id:${placeId}` : 'https://business.google.com/reviews',
    label: 'Voir la fiche',
  };

  if (l.includes('photo')) return {
    type:  'gbp',
    path:  ['business.google.com', 'Modifier le profil', 'Photos'],
    link:  'https://business.google.com/',
    label: 'Ajouter des photos',
  };

  if (l.includes('description')) return {
    type:  'gbp',
    path:  ['business.google.com', 'Modifier le profil', 'Informations', 'Description'],
    link:  'https://business.google.com/',
    label: 'Modifier le profil',
  };

  if (l.includes('horaire') || l.includes('ouverture')) return {
    type:  'gbp',
    path:  ['business.google.com', 'Modifier le profil', 'Horaires'],
    link:  'https://business.google.com/',
    label: 'Modifier les horaires',
  };

  if (l.includes('téléphone') || l.includes('telephone')) return {
    type:  'gbp',
    path:  ['business.google.com', 'Modifier le profil', 'Coordonnées', 'Téléphone'],
    link:  'https://business.google.com/',
    label: 'Ajouter le téléphone',
  };

  if (l.includes('site web')) return {
    type:  'gbp',
    path:  ['business.google.com', 'Modifier le profil', 'Coordonnées', 'Site web'],
    link:  'https://business.google.com/',
    label: 'Ajouter le site web',
  };

  return {
    type:  'gbp',
    path:  ['business.google.com', 'Modifier le profil'],
    link:  'https://business.google.com/',
    label: 'Ouvrir Google Business',
  };
}

const TYPE_COLOR = {
  review: '#fbbf24',
  gbp:    '#4db6ff',
  maps:   '#4ade80',
};

/* ─── Chemin visuel GBP ─── */
function FixPath({ location, placeId, copied, onCopy }) {
  const col = TYPE_COLOR[location.type] || '#4db6ff';
  return (
    <div style={{ marginTop:10, background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'10px 14px' }}>
      {/* Breadcrumb */}
      <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:4, marginBottom:8 }}>
        <span style={{ fontSize:'0.62rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginRight:4 }}>📍 Où corriger :</span>
        {location.path.map((step, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ color:'#2a2a2a', fontSize:'0.75rem' }}>›</span>}
            <span style={{
              background: i === location.path.length - 1 ? `${col}15` : 'transparent',
              border:     i === location.path.length - 1 ? `1px solid ${col}30` : 'none',
              color:      i === location.path.length - 1 ? col : '#555',
              fontSize:   '0.72rem', fontWeight: i === location.path.length - 1 ? 700 : 500,
              padding:    i === location.path.length - 1 ? '2px 8px' : '2px 4px',
              borderRadius: 5,
            }}>{step}</span>
          </React.Fragment>
        ))}
      </div>

      {/* Lien direct */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
        <a href={location.link} target="_blank" rel="noopener noreferrer"
          style={{ display:'inline-flex', alignItems:'center', gap:5, background:`${col}10`, border:`1px solid ${col}30`, color:col, borderRadius:7, padding:'5px 12px', fontSize:'0.72rem', fontWeight:700, textDecoration:'none', flexShrink:0 }}>
          🔗 {location.label} ↗
        </a>

        {/* Lien avis spécial : affiche l'URL copiable */}
        {location.type === 'review' && location.reviewLink && (
          <>
            <span style={{ color:'#2a2a2a', fontSize:'0.7rem' }}>ou partager ce lien avec vos clients :</span>
            <div style={{ display:'flex', alignItems:'center', gap:6, flex:1, minWidth:0 }}>
              <code style={{ fontSize:'0.65rem', color:'#555', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:5, padding:'3px 8px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0 }}>
                {location.reviewLink}
              </code>
              <button onClick={() => onCopy(location.reviewLink, 'review_url')}
                style={{ background: copied === 'review_url' ? 'rgba(74,222,128,0.1)' : 'transparent', border:`1px solid ${copied === 'review_url' ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.1)'}`, color: copied === 'review_url' ? '#4ade80' : '#555', borderRadius:6, padding:'3px 10px', cursor:'pointer', fontSize:'0.68rem', fontWeight:700, flexShrink:0 }}>
                {copied === 'review_url' ? '✓ Copié' : 'Copier'}
              </button>
              <a href={location.reviewLink} target="_blank" rel="noopener noreferrer"
                style={{ background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.25)', color:'#fbbf24', borderRadius:6, padding:'3px 10px', fontSize:'0.68rem', fontWeight:700, textDecoration:'none', flexShrink:0 }}>
                Tester ↗
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Score circle ─── */
function ScoreCircle({ score }) {
  const c     = score >= 70 ? '#4ade80' : score >= 40 ? '#fbbf24' : '#f87171';
  const label = score >= 70 ? 'BON'     : score >= 40 ? 'MOYEN'   : 'FAIBLE';
  const r = 56, circ = 2 * Math.PI * r;
  return (
    <div style={{ position:'relative', width:130, height:130, flexShrink:0 }}>
      <svg width="130" height="130" style={{ transform:'rotate(-90deg)' }}>
        <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10"/>
        <circle cx="65" cy="65" r={r} fill="none" stroke={c} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - score / 100)}
          strokeLinecap="round" style={{ transition:'stroke-dashoffset 1s ease' }}/>
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
        <span style={{ fontSize:'2rem', fontWeight:900, color:c, lineHeight:1 }}>{score}</span>
        <span style={{ fontSize:'0.55rem', color:c+'99', fontWeight:700, letterSpacing:'0.08em' }}>/100</span>
        <span style={{ fontSize:'0.55rem', color:c, fontWeight:800, letterSpacing:'0.1em', marginTop:2 }}>{label}</span>
      </div>
    </div>
  );
}

function InfoRow({ label, value, accent }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'9px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', gap:12 }}>
      <span style={{ color:'#444', fontSize:'0.75rem', flexShrink:0 }}>{label}</span>
      <span style={{ color: accent || '#888', fontSize:'0.78rem', fontWeight:600, textAlign:'right', wordBreak:'break-word', maxWidth:'65%' }}>{value || '—'}</span>
    </div>
  );
}

function StarRating({ rating }) {
  if (rating == null) return <span style={{ color:'#444', fontSize:'0.78rem' }}>—</span>;
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:3 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ fontSize:'0.9rem', color: i <= full ? '#fbbf24' : (i === full+1 && half) ? '#fbbf24' : '#333' }}>
          {i <= full ? '★' : (i === full+1 && half) ? '⯨' : '☆'}
        </span>
      ))}
      <span style={{ color:'#fbbf24', fontSize:'0.8rem', fontWeight:800, marginLeft:4 }}>{rating.toFixed(1)}</span>
    </span>
  );
}

/* ─── Templates copiables ─── */
const COPY_TEMPLATES = {
  review_request: (name) =>
`Bonjour,

Merci de votre confiance envers ${name} !

Si vous êtes satisfait(e) de notre service, nous vous invitons à laisser un avis Google — cela nous aide énormément et ne prend que 30 secondes.

👉 [Lien direct vers votre fiche Google]

Merci beaucoup !
L'équipe ${name}`,

  photos_checklist: () =>
`📸 Checklist photos Google Business Profile :

□ Logo de l'entreprise (format carré, fond uni)
□ Photo de couverture (1080×608 px recommandé)
□ Extérieur du bâtiment (façade, enseigne, stationnement)
□ Intérieur (salle, comptoir, espace de travail)
□ L'équipe en action (sourires, professionnalisme)
□ Produits ou services phares
□ Photos récentes (moins de 6 mois)

Objectif : 15 photos minimum pour un impact maximal.`,

  description_template: (name, city) =>
`${name} est une entreprise spécialisée dans [votre secteur] établie à ${city}.

Nous offrons [vos services principaux] à une clientèle [cible] depuis [année de fondation].

Notre engagement : [votre proposition de valeur unique — rapidité, qualité, prix, expertise, etc.].

Nous desservons ${city} et les environs. Contactez-nous pour un devis gratuit.

📍 ${city} | 📞 [votre numéro] | 🌐 [votre site web]`,

  hours_reminder: () =>
`⏰ Rappel : mettez vos horaires à jour sur Google Business Profile

1. Connectez-vous sur business.google.com
2. Sélectionnez votre établissement
3. Cliquez sur "Modifier le profil"
4. Renseignez vos horaires d'ouverture
5. Ajoutez les horaires spéciaux (fêtes, vacances)

Des horaires corrects = moins de clients qui se déplacent pour rien = meilleure note Google.`,
};

/* ─── Export PDF ───
   CORRECTIF : window.open sans le 3e paramètre 'noopener,noreferrer'
   car ce flag bloque l'accès à win.document et rend la page blanche.
   La sécurité est assurée par l'échappement HTML via esc() sur toutes les valeurs.
*/
function exportPDF(d, city) {
  const scoreColor = d.score >= 70 ? '#16a34a' : d.score >= 40 ? '#d97706' : '#dc2626';
  const scoreLabel = d.score >= 70 ? 'BON'     : d.score >= 40 ? 'MOYEN'   : 'FAIBLE';

  const infoRows = [
    ['Établissement', d.name],
    ['Adresse',       d.address],
    ['Téléphone',     d.phone],
    ['Site web',      d.website],
    ['Note',          d.rating != null ? `${d.rating}/5` : null],
    ['Avis Google',   `${d.reviews} avis`],
    ['Photos',        `${d.photos} photo${d.photos !== 1 ? 's' : ''}`],
    ['Horaires',      d.hasHours       ? 'Renseignés ✓' : 'Manquants'],
    ['Description',   d.hasDescription ? 'Présente ✓'  : 'Manquante'],
  ].map(([label, val]) => `
    <tr>
      <td style="padding:8px 10px;color:#888;font-size:12px;border-bottom:1px solid #f0f0f0;white-space:nowrap;">${esc(label)}</td>
      <td style="padding:8px 10px;color:#111;font-size:13px;font-weight:600;border-bottom:1px solid #f0f0f0;word-break:break-all;">${esc(val ?? '—')}</td>
    </tr>`).join('');

  const issueRows = (d.issues || []).length === 0
    ? `<div style="color:#16a34a;font-size:13px;padding:12px 0;">✅ Aucune vulnérabilité détectée — profil complet !</div>`
    : (d.issues || []).map(iss => `
    <div style="background:#fff5f5;border-left:4px solid #dc2626;border-radius:4px;padding:12px 14px;margin-bottom:10px;">
      <div style="display:table;width:100%;margin-bottom:6px;">
        <span style="display:table-cell;font-size:13px;font-weight:700;color:#dc2626;">${esc(iss.label)}</span>
        <span style="display:table-cell;text-align:right;font-size:11px;font-weight:700;color:#ef4444;">${esc(String(iss.impact))} pts</span>
      </div>
      <div style="font-size:12px;color:#555;">→ ${esc(iss.action)}</div>
    </div>`).join('');

  const strengthRows = (d.strengths || []).map(s => `
    <div style="display:table;width:100%;padding:7px 0;border-bottom:1px solid #f0fdf4;">
      <span style="display:table-cell;width:20px;color:#22c55e;font-weight:900;">✓</span>
      <span style="display:table-cell;font-size:13px;color:#166534;">${esc(s)}</span>
    </div>`).join('');

  const aiRows = (d.aiRecommendations || []).map((r, i) => `
    <div style="display:table;width:100%;padding:8px 0;border-bottom:1px solid #f5f3ff;">
      <span style="display:table-cell;width:24px;color:#7c3aed;font-weight:900;font-size:13px;">${i + 1}.</span>
      <span style="display:table-cell;font-size:13px;color:#374151;line-height:1.5;">${esc(r)}</span>
    </div>`).join('');

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Rapport Fiche Google — ${esc(d.name)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, Arial, sans-serif; max-width: 780px; margin: 0 auto; padding: 48px 40px; color: #111; line-height: 1.5; }
  h2 { font-size: 11px; font-weight: 700; color: #999; text-transform: uppercase; letter-spacing: .1em; margin: 28px 0 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; }
  table { width: 100%; border-collapse: collapse; background: #f9fafb; border-radius: 10px; overflow: hidden; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; color: #999; font-size: 11px; display: table; width: 100%; }
  @media print { body { padding: 20px; } }
</style>
</head>
<body>

<div style="display:table;width:100%;margin-bottom:32px;padding-bottom:24px;border-bottom:2px solid #e5e7eb;">
  <div style="display:table-cell;vertical-align:top;">
    <div style="font-size:10px;text-transform:uppercase;letter-spacing:.12em;color:#999;margin-bottom:6px;">Ucreative — Google Business Profile</div>
    <h1 style="font-size:22px;font-weight:900;margin-bottom:4px;">${esc(d.name)}</h1>
    <div style="color:#555;font-size:13px;">${esc(d.address || '')}</div>
  </div>
  <div style="display:table-cell;text-align:right;vertical-align:top;width:160px;">
    <div style="font-size:64px;font-weight:900;color:${scoreColor};line-height:1;">${d.score}</div>
    <div style="font-size:12px;color:#999;">/100 — ${scoreLabel}</div>
    <div style="font-size:12px;color:#999;margin-top:2px;">${(d.issues || []).length} vulnérabilité${(d.issues||[]).length !== 1 ? 's' : ''} détectée${(d.issues||[]).length !== 1 ? 's' : ''}</div>
  </div>
</div>

<h2>Informations de la fiche</h2>
<table>${infoRows}</table>

<h2>Vulnérabilités détectées (${(d.issues || []).length})</h2>
${issueRows}

${(d.strengths || []).length > 0 ? `<h2>Points forts (${d.strengths.length})</h2>${strengthRows}` : ''}

${(d.aiRecommendations || []).length > 0 ? `
<h2>Recommandations IA (Claude)</h2>
<div style="background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:14px 16px;">${aiRows}</div>` : ''}

<div class="footer">
  <span style="display:table-cell;">Rapport généré par <strong>Ucreative</strong></span>
  <span style="display:table-cell;text-align:right;">${new Date().toLocaleDateString('fr-CA', { year:'numeric', month:'long', day:'numeric' })}</span>
</div>

</body>
</html>`;

  // CORRECTIF PDF : pas de 3e paramètre — 'noopener' bloque win.document et vide la page
  const win = window.open('', '_blank');
  if (!win) { alert('Popup bloqué — autorisez les popups pour ce site.'); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

const SECTORS = [
  'Restaurant / Café','Bar / Pub','Boulangerie / Pâtisserie','Épicerie / Dépanneur',
  'Salon de coiffure','Barbier','Spa / Esthétique','Massage / Bien-être',
  'Plombier','Électricien','Serrurier','Rénovation / Construction',
  'Clinique médicale','Dentiste','Chiropraticien','Optométriste','Pharmacie',
  'Avocat','Comptable / Comptabilité','Notaire','Agence immobilière',
  'Garage automobile','Carrosserie','Vente de véhicules',
  'Gym / Centre sportif','Yoga / Pilates','Studio de danse',
  'Hôtel / Auberge','Nettoyage / Entretien ménager','Déménagement',
  'École / Académie','Garderie / CPE','Tuteur / Formation',
  'Agence marketing / Web','Imprimerie','Photographe',
  'Fleuriste','Animalerie','Vétérinaire',
  'Autre',
];

/* ─── Composant principal ─── */
export default function GmbSection() {
  const [tab,          setTab]          = useState('analyze');

  // ── Onglet Analyser ──
  const [businessName, setBusinessName] = useState('');
  const [city,         setCity]         = useState('');
  const [loading,      setLoading]      = useState(false);
  const [data,         setData]         = useState(null);
  const [error,        setError]        = useState(null);
  const [copied,       setCopied]       = useState(null);
  const [openHours,    setOpenHours]    = useState(false);

  // ── Onglet Générer ──
  const [genName,      setGenName]      = useState('');
  const [genCity,      setGenCity]      = useState('');
  const [genSector,    setGenSector]    = useState('');
  const [genPhone,     setGenPhone]     = useState('');
  const [genWebsite,   setGenWebsite]   = useState('');
  const [genHours,     setGenHours]     = useState('');
  const [genAbout,     setGenAbout]     = useState('');
  const [genLoading,   setGenLoading]   = useState(false);
  const [genFiche,     setGenFiche]     = useState(null);
  const [genError,     setGenError]     = useState(null);
  const [genSaving,    setGenSaving]    = useState(false);
  const [genSavedId,   setGenSavedId]   = useState(null);
  const [savedFiches,  setSavedFiches]  = useState([]);
  const [loadingList,  setLoadingList]  = useState(false);
  const [copiedGen,    setCopiedGen]    = useState(null);

  const loadSavedFiches = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data: d } = await ax.get('/api/gmb/fiches');
      setSavedFiches(d.fiches || []);
    } catch { }
    setLoadingList(false);
  }, []);

  useEffect(() => { if (tab === 'generate') loadSavedFiches(); }, [tab, loadSavedFiches]);

  function copyGen(text, key) {
    navigator.clipboard?.writeText(text);
    setCopiedGen(key);
    setTimeout(() => setCopiedGen(null), 2200);
  }

  const CopyBtnGen = ({ text, id, label = 'Copier' }) => (
    <button onClick={() => copyGen(text, id)}
      style={{ background: copiedGen === id ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)',
        border:`1px solid ${copiedGen === id ? 'rgba(74,222,128,0.35)' : 'rgba(255,255,255,0.1)'}`,
        color: copiedGen === id ? '#4ade80' : '#888', borderRadius:8, padding:'6px 14px',
        cursor:'pointer', fontSize:'0.72rem', fontWeight:700, flexShrink:0, transition:'all 0.15s', whiteSpace:'nowrap' }}>
      {copiedGen === id ? '✓ Copié' : label}
    </button>
  );

  async function generate() {
    if (!genName.trim() || !genCity.trim() || !genSector) return;
    setGenLoading(true); setGenError(null); setGenFiche(null); setGenSavedId(null);
    try {
      const { data: d } = await ax.post('/api/gmb/generate', {
        businessName: genName.trim(), city: genCity.trim(), sector: genSector,
        phone: genPhone.trim(), website: genWebsite.trim(),
        hours: genHours.trim(), about: genAbout.trim(),
      });
      setGenFiche(d.fiche);
    } catch (e) {
      setGenError(e.response?.data?.error || 'Erreur génération.');
    }
    setGenLoading(false);
  }

  async function saveFiche() {
    if (!genFiche) return;
    setGenSaving(true);
    try {
      const { data: d } = await ax.post('/api/gmb/save-fiche', {
        fiche: genFiche,
        meta: { businessName: genName.trim(), city: genCity.trim(), sector: genSector },
      });
      setGenSavedId(d.id);
      loadSavedFiches();
    } catch { }
    setGenSaving(false);
  }

  async function deleteFiche(id) {
    try {
      await ax.delete(`/api/gmb/fiches/${id}`);
      setSavedFiches(f => f.filter(x => x.id !== id));
    } catch { }
  }

  function loadFiche(item) {
    setGenName(item.meta?.businessName || '');
    setGenCity(item.meta?.city || '');
    setGenSector(item.meta?.sector || '');
    setGenFiche(item.fiche);
    setGenSavedId(item.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function analyze() {
    const name = businessName.trim();
    const c    = city.trim();
    if (!name || !c) return;
    setLoading(true); setError(null); setData(null);
    try {
      const { data: d } = await ax.post('/api/gmb/analyze', { businessName: name, city: c });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'analyse.');
    } finally { setLoading(false); }
  }

  function copy(text, key) {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2200);
  }

  const CopyBtn = ({ text, id, label = 'Copier' }) => (
    <button onClick={() => copy(text, id)}
      style={{ background: copied === id ? 'rgba(74,222,128,0.1)' : 'transparent',
        border:`1px solid ${copied === id ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.1)'}`,
        color: copied === id ? '#4ade80' : '#555', borderRadius:7, padding:'5px 12px',
        cursor:'pointer', fontSize:'0.72rem', fontWeight:700, flexShrink:0, transition:'all 0.15s' }}>
      {copied === id ? '✓ Copié' : label}
    </button>
  );

  const d = data;
  const scoreColor = d ? (d.score >= 70 ? '#4ade80' : d.score >= 40 ? '#fbbf24' : '#f87171') : null;

  const inp = {
    background:'#0a0a0d', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10,
    padding:'13px 16px', color:'#EAEAEA', fontSize:'0.92rem', outline:'none',
    width:'100%', boxSizing:'border-box',
  };

  return (
    <div style={{ padding:32, maxWidth:960 }}>

      {/* ── Header ── */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:'0.63rem', color:'#333', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:6 }}>Ucreative</div>
        <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em', margin:'0 0 6px' }}>Fiche Google</h1>
        <p style={{ color:'#333', fontSize:'0.83rem', margin:0 }}>Analyse et génération de fiches Google Business Profile</p>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', gap:4, background:'rgba(255,255,255,0.04)', borderRadius:12, padding:4, marginBottom:28, width:'fit-content' }}>
        {[{ id:'analyze', label:'🔍 Analyser une fiche' }, { id:'generate', label:'✨ Générer une fiche' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:'9px 20px', borderRadius:9, border:'none', fontWeight:700, fontSize:'0.84rem', cursor:'pointer', transition:'all 0.2s', fontFamily:'inherit',
              background: tab === t.id ? '#C8FF00' : 'transparent',
              color:      tab === t.id ? '#000' : '#555' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════ ONGLET GÉNÉRER ══════════════════════ */}
      {tab === 'generate' && (
        <div>
          {/* Formulaire */}
          <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'24px 28px', marginBottom:20 }}>
            <p style={{ fontSize:'0.65rem', color:'#555', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:18 }}>Informations de l'entreprise</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:'0.7rem', color:'#555', fontWeight:600, display:'block', marginBottom:6 }}>Nom de l'entreprise *</label>
                <input style={inp} value={genName} onChange={e => setGenName(e.target.value)} placeholder="Ex : Plomberie Tremblay" maxLength={100} />
              </div>
              <div>
                <label style={{ fontSize:'0.7rem', color:'#555', fontWeight:600, display:'block', marginBottom:6 }}>Ville *</label>
                <input style={inp} value={genCity} onChange={e => setGenCity(e.target.value)} placeholder="Ex : Montréal, QC" maxLength={80} />
              </div>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:'0.7rem', color:'#555', fontWeight:600, display:'block', marginBottom:6 }}>Secteur d'activité *</label>
              <select style={{ ...inp, appearance:'none', cursor:'pointer' }} value={genSector} onChange={e => setGenSector(e.target.value)}>
                <option value="">— Choisir un secteur —</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:'0.7rem', color:'#555', fontWeight:600, display:'block', marginBottom:6 }}>Téléphone</label>
                <input style={inp} value={genPhone} onChange={e => setGenPhone(e.target.value)} placeholder="514 000-0000" maxLength={20} />
              </div>
              <div>
                <label style={{ fontSize:'0.7rem', color:'#555', fontWeight:600, display:'block', marginBottom:6 }}>Site web</label>
                <input style={inp} value={genWebsite} onChange={e => setGenWebsite(e.target.value)} placeholder="https://monsite.ca" maxLength={200} />
              </div>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:'0.7rem', color:'#555', fontWeight:600, display:'block', marginBottom:6 }}>Horaires (optionnel)</label>
              <input style={inp} value={genHours} onChange={e => setGenHours(e.target.value)} placeholder="Ex : Lun-Ven 8h-18h, Sam 9h-15h, Dim fermé" maxLength={200} />
            </div>
            <div style={{ marginBottom:18 }}>
              <label style={{ fontSize:'0.7rem', color:'#555', fontWeight:600, display:'block', marginBottom:6 }}>Description de l'activité (optionnel)</label>
              <textarea style={{ ...inp, minHeight:80, resize:'vertical', lineHeight:1.5 }} value={genAbout} onChange={e => setGenAbout(e.target.value)}
                placeholder="Décris ce que tu fais, ta clientèle, ta spécialité, ton expérience..." maxLength={600} />
            </div>
            <button onClick={generate} disabled={genLoading || !genName.trim() || !genCity.trim() || !genSector}
              style={{ background: (genLoading || !genName.trim() || !genCity.trim() || !genSector) ? '#111' : '#C8FF00',
                color: (genLoading || !genName.trim() || !genCity.trim() || !genSector) ? '#333' : '#000',
                border:'none', borderRadius:10, padding:'13px 32px', fontWeight:900, fontSize:'0.9rem',
                cursor: (genLoading || !genName.trim() || !genCity.trim() || !genSector) ? 'default' : 'pointer', transition:'all 0.15s' }}>
              {genLoading ? '⏳ Génération IA en cours...' : '✨ Générer la fiche complète'}
            </button>
          </div>

          {genError && (
            <div style={{ background:'rgba(248,113,113,0.06)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:12, padding:'14px 18px', marginBottom:16, color:'#f87171', fontSize:'0.85rem' }}>
              ⚠️ {genError}
            </div>
          )}

          {/* ── Résultat généré ── */}
          {genFiche && !genLoading && (() => {
            const f = genFiche;
            const Section = ({ title, icon, children, accent = '#C8FF00' }) => (
              <div style={{ background:'#0d0d10', border:`1px solid rgba(255,255,255,0.07)`, borderRadius:14, overflow:'hidden', marginBottom:14 }}>
                <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center', gap:8 }}>
                  <span>{icon}</span>
                  <span style={{ fontSize:'0.68rem', fontWeight:800, color: accent, letterSpacing:'0.08em', textTransform:'uppercase' }}>{title}</span>
                </div>
                <div style={{ padding:'16px 20px' }}>{children}</div>
              </div>
            );
            const Block = ({ label, value, id }) => (
              <div style={{ marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:'0.7rem', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</span>
                  <CopyBtnGen text={value} id={id} />
                </div>
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'10px 14px', fontSize:'0.85rem', color:'#EAEAEA', lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
                  {value}
                </div>
              </div>
            );

            return (
              <>
                {/* Actions bar */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                  <div style={{ flex:1 }}>
                    <p style={{ color:'#4ade80', fontWeight:800, fontSize:'0.9rem', marginBottom:2 }}>✅ Fiche générée — 100% complète</p>
                    <p style={{ color:'#555', fontSize:'0.75rem' }}>Copie chaque section directement dans Google Business Profile</p>
                  </div>
                  <button onClick={saveFiche} disabled={genSaving || !!genSavedId}
                    style={{ padding:'10px 22px', borderRadius:10, border:'none', fontWeight:800, fontSize:'0.82rem', cursor: genSaving || genSavedId ? 'default' : 'pointer', transition:'all 0.15s',
                      background: genSavedId ? 'rgba(74,222,128,0.1)' : genSaving ? '#111' : 'rgba(200,255,0,0.15)',
                      color: genSavedId ? '#4ade80' : genSaving ? '#333' : '#C8FF00',
                      border: `1px solid ${genSavedId ? 'rgba(74,222,128,0.3)' : 'rgba(200,255,0,0.2)'}` }}>
                    {genSavedId ? '✓ Sauvegardée' : genSaving ? '…' : '💾 Sauvegarder'}
                  </button>
                </div>

                {/* Nom + Catégories */}
                <Section title="Identité GBP" icon="🏷" accent="#C8FF00">
                  <Block label="Nom formaté" value={f.nameFormatted} id="gn_name" />
                  <Block label="Catégorie principale" value={f.primaryCategory} id="gn_cat1" />
                  <div style={{ marginBottom:4 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                      <span style={{ fontSize:'0.7rem', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>Catégories secondaires</span>
                      <CopyBtnGen text={f.secondaryCategories?.join(', ')} id="gn_cats" />
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                      {f.secondaryCategories?.map((c, i) => (
                        <span key={i} style={{ background:'rgba(200,255,0,0.07)', border:'1px solid rgba(200,255,0,0.15)', color:'#C8FF00', borderRadius:8, padding:'4px 12px', fontSize:'0.78rem', fontWeight:600 }}>{c}</span>
                      ))}
                    </div>
                  </div>
                </Section>

                {/* Descriptions */}
                <Section title="Descriptions" icon="📝" accent="#60a5fa">
                  <Block label={`Description principale (${f.description?.length || 0}/750 car.)`} value={f.description} id="gn_desc" />
                  <Block label="Accroche courte" value={f.shortDescription} id="gn_short" />
                </Section>

                {/* Services */}
                <Section title="Services (10)" icon="⚙️" accent="#a78bfa">
                  <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:10 }}>
                    <CopyBtnGen text={f.services?.map(s => `${s.name}\n${s.description}`).join('\n\n')} id="gn_all_services" label="Copier tous" />
                  </div>
                  {f.services?.map((s, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'10px 0', borderBottom: i < f.services.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                      <span style={{ color:'#a78bfa', fontWeight:900, fontSize:'0.78rem', flexShrink:0, width:22, paddingTop:2 }}>{i+1}.</span>
                      <div style={{ flex:1 }}>
                        <p style={{ fontWeight:700, fontSize:'0.84rem', color:'#EAEAEA', marginBottom:3 }}>{s.name}</p>
                        <p style={{ fontSize:'0.78rem', color:'#666', lineHeight:1.5 }}>{s.description}</p>
                      </div>
                      <CopyBtnGen text={`${s.name}\n${s.description}`} id={`gn_s${i}`} />
                    </div>
                  ))}
                </Section>

                {/* Q&A */}
                <Section title="Questions & Réponses (6)" icon="💬" accent="#fbbf24">
                  <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:10 }}>
                    <CopyBtnGen text={f.qa?.map(q => `Q: ${q.q}\nR: ${q.a}`).join('\n\n')} id="gn_all_qa" label="Copier tous" />
                  </div>
                  {f.qa?.map((q, i) => (
                    <div key={i} style={{ padding:'10px 0', borderBottom: i < f.qa.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                        <p style={{ fontWeight:700, fontSize:'0.82rem', color:'#fbbf24', flex:1 }}>Q: {q.q}</p>
                        <CopyBtnGen text={`Q: ${q.q}\nR: ${q.a}`} id={`gn_q${i}`} />
                      </div>
                      <p style={{ fontSize:'0.8rem', color:'#888', lineHeight:1.5 }}>R: {q.a}</p>
                    </div>
                  ))}
                </Section>

                {/* Premier post + Mots-clés + Photos */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                  <Section title="Premier post Google" icon="📢" accent="#f472b6">
                    <Block label={`Texte (${f.firstPost?.length || 0}/280 car.)`} value={f.firstPost} id="gn_post" />
                  </Section>
                  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                    <Section title="Mots-clés (15)" icon="🔑" accent="#4ade80">
                      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
                        <CopyBtnGen text={f.keywords?.join(', ')} id="gn_kw" />
                      </div>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                        {f.keywords?.map((k, i) => (
                          <span key={i} style={{ background:'rgba(74,222,128,0.07)', border:'1px solid rgba(74,222,128,0.15)', color:'#4ade80', borderRadius:6, padding:'3px 10px', fontSize:'0.74rem', fontWeight:600 }}>{k}</span>
                        ))}
                      </div>
                    </Section>
                    <Section title="Photos à prendre (8)" icon="📸" accent="#60a5fa">
                      {f.photoTypes?.map((p, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom: i < f.photoTypes.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                          <span style={{ color:'#60a5fa', fontSize:'0.8rem' }}>📷</span>
                          <span style={{ fontSize:'0.8rem', color:'#888' }}>{p}</span>
                        </div>
                      ))}
                    </Section>
                  </div>
                </div>

                {/* Attributs */}
                <Section title="Attributs GBP à activer" icon="✅" accent="#4ade80">
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {f.attributes?.map((a, i) => (
                      <span key={i} style={{ background:'rgba(74,222,128,0.07)', border:'1px solid rgba(74,222,128,0.15)', color:'#4ade80', borderRadius:8, padding:'5px 14px', fontSize:'0.8rem', fontWeight:600 }}>✓ {a}</span>
                    ))}
                  </div>
                </Section>

                {/* Guide de déploiement */}
                <div style={{ background:'rgba(200,255,0,0.04)', border:'1px solid rgba(200,255,0,0.12)', borderRadius:14, padding:'20px 24px', marginBottom:14 }}>
                  <p style={{ fontSize:'0.68rem', fontWeight:800, color:'#C8FF00', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:14 }}>🚀 Guide de déploiement rapide</p>
                  {[
                    ['1', 'Créer/ouvrir la fiche', 'Va sur business.google.com → "Ajouter un établissement" ou sélectionne ta fiche existante'],
                    ['2', 'Modifier le profil', 'Copie le nom formaté, la catégorie principale + secondaires, le téléphone, le site web'],
                    ['3', 'Coller la description', 'Informations → Description → colle le texte de 750 caractères généré'],
                    ['4', 'Ajouter les services', 'Services → Ajouter → copie chacun des 10 services avec leur description'],
                    ['5', 'Configurer les horaires', 'Heures d\'ouverture → renseigne tes horaires complets'],
                    ['6', 'Uploader les photos', 'Photos → Ajouter → suis le guide des 8 types de photos ci-dessus'],
                    ['7', 'Créer le premier post', 'Publications → Créer → colle le texte du premier post'],
                    ['8', 'Activer les attributs', 'Modifier le profil → Informations complémentaires → coche les attributs listés'],
                    ['9', 'Répondre à la Q&A', 'Questions et réponses → Poser une question → puis réponds toi-même (stratégie proactive)'],
                  ].map(([n, t, d]) => (
                    <div key={n} style={{ display:'flex', gap:14, padding:'8px 0', borderBottom: n !== '9' ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                      <span style={{ background:'rgba(200,255,0,0.12)', color:'#C8FF00', fontWeight:900, fontSize:'0.75rem', width:24, height:24, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>{n}</span>
                      <div>
                        <p style={{ fontWeight:700, fontSize:'0.82rem', color:'#EAEAEA', marginBottom:2 }}>{t}</p>
                        <p style={{ fontSize:'0.75rem', color:'#555', lineHeight:1.5 }}>{d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}

          {/* ── Fiches sauvegardées ── */}
          <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, overflow:'hidden', marginTop:28 }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:'0.68rem', fontWeight:800, color:'#555', letterSpacing:'0.08em', textTransform:'uppercase' }}>💾 Fiches sauvegardées</span>
              <button onClick={loadSavedFiches} style={{ background:'transparent', border:'none', color:'#444', cursor:'pointer', fontSize:'0.75rem', fontWeight:600 }}>
                {loadingList ? '…' : '↻ Rafraîchir'}
              </button>
            </div>
            {savedFiches.length === 0 ? (
              <div style={{ padding:'20px', color:'#333', fontSize:'0.8rem', textAlign:'center' }}>
                {loadingList ? 'Chargement…' : 'Aucune fiche sauvegardée.'}
              </div>
            ) : (
              savedFiches.map(item => (
                <div key={item.id} style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', alignItems:'center', gap:14 }}>
                  <div style={{ flex:1 }}>
                    <p style={{ fontWeight:700, fontSize:'0.85rem', color:'#EAEAEA', marginBottom:2 }}>{item.meta?.businessName}</p>
                    <p style={{ fontSize:'0.72rem', color:'#444' }}>{item.meta?.city} · {item.meta?.sector} · {item.createdAt ? new Date(item.createdAt).toLocaleDateString('fr-CA') : ''}</p>
                  </div>
                  <button onClick={() => loadFiche(item)}
                    style={{ padding:'6px 16px', borderRadius:8, border:'1px solid rgba(200,255,0,0.2)', background:'rgba(200,255,0,0.07)', color:'#C8FF00', fontWeight:700, fontSize:'0.75rem', cursor:'pointer' }}>
                    Charger
                  </button>
                  <button onClick={() => deleteFiche(item.id)}
                    style={{ padding:'6px 12px', borderRadius:8, border:'1px solid rgba(248,113,113,0.2)', background:'transparent', color:'#f87171', fontWeight:700, fontSize:'0.75rem', cursor:'pointer' }}>
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════ ONGLET ANALYSER ══════════════════════ */}
      {tab === 'analyze' && <>

      {/* ── Inputs ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:10, marginBottom:28 }}>
        <input style={inp} value={businessName} maxLength={100}
          onChange={e => setBusinessName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && analyze()}
          placeholder="Nom de l'entreprise" />
        <input style={inp} value={city} maxLength={80}
          onChange={e => setCity(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && analyze()}
          placeholder="Ville (ex: Montréal, QC)" />
        <button onClick={analyze} disabled={loading || !businessName.trim() || !city.trim()}
          style={{ background: loading || !businessName.trim() || !city.trim() ? '#111' : '#C8FF00',
            color:  loading || !businessName.trim() || !city.trim() ? '#333' : '#000',
            border:'none', borderRadius:10, padding:'13px 28px', fontWeight:900,
            fontSize:'0.9rem', cursor: loading || !businessName.trim() || !city.trim() ? 'default' : 'pointer',
            whiteSpace:'nowrap', transition:'all 0.15s' }}>
          {loading ? '⏳ Analyse...' : '🔍 Analyser'}
        </button>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'28px 24px', textAlign:'center' }}>
          <div style={{ fontSize:'1.5rem', marginBottom:12 }}>📍</div>
          <div style={{ color:'#EAEAEA', fontWeight:700, marginBottom:6 }}>Analyse en cours...</div>
          <div style={{ color:'#444', fontSize:'0.8rem', lineHeight:1.8 }}>Recherche Google · Scoring · Suggestions IA</div>
        </div>
      )}

      {/* ── Erreur ── */}
      {error && (
        <div style={{ background:'rgba(248,113,113,0.06)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:12, padding:'16px 20px' }}>
          <div style={{ color:'#f87171', fontWeight:700 }}>⚠️ Erreur</div>
          <div style={{ color:'#f87171cc', fontSize:'0.82rem', marginTop:4 }}>{error}</div>
        </div>
      )}

      {/* ══ RÉSULTATS ══ */}
      {d && !loading && (
        <>
          {/* ── Score card ── */}
          <div style={{ background:'#0d0d10', border:`1px solid ${scoreColor}22`, borderRadius:14, padding:'24px 28px', marginBottom:14, display:'flex', alignItems:'center', gap:28, flexWrap:'wrap' }}>
            <ScoreCircle score={d.score} />
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ color:'#444', fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>Score Fiche Google</div>
              <div style={{ color:'#EAEAEA', fontWeight:800, fontSize:'1.1rem', marginBottom:2 }}>{d.name}</div>
              <div style={{ color:'#555', fontSize:'0.8rem', marginBottom:10 }}>{d.address}</div>
              <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                {d.rating != null && (
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <StarRating rating={d.rating} />
                    <span style={{ color:'#555', fontSize:'0.75rem' }}>({d.reviews} avis)</span>
                  </div>
                )}
                {d.photos > 0 && <span style={{ color:'#555', fontSize:'0.75rem' }}>📷 {d.photos} photos</span>}
                {safeUrl(d.mapsUrl) && (
                  <a href={safeUrl(d.mapsUrl)} target="_blank" rel="noopener noreferrer"
                    style={{ color:'#4db6ff', fontSize:'0.75rem', textDecoration:'none', fontWeight:600 }}>
                    Voir sur Maps →
                  </a>
                )}
              </div>
            </div>
            <button onClick={() => exportPDF(d, city)}
              style={{ background:'#C8FF00', color:'#000', border:'none', borderRadius:10, padding:'12px 24px', fontWeight:900, fontSize:'0.88rem', cursor:'pointer', flexShrink:0 }}>
              📄 Télécharger PDF
            </button>
          </div>

          {/* ── VULNÉRABILITÉS — toujours affiché ── */}
          <div style={{ background: d.issues?.length === 0 ? 'rgba(74,222,128,0.03)' : '#09080a', border:`1px solid ${d.issues?.length === 0 ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.2)'}`, borderRadius:14, overflow:'hidden', marginBottom:14 }}>

            {/* En-tête */}
            <div style={{ padding:'14px 22px', borderBottom:`1px solid ${d.issues?.length === 0 ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)'}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', color: d.issues?.length === 0 ? '#4ade80' : '#f87171' }}>
                  {d.issues?.length === 0 ? '✅ Aucune vulnérabilité' : `⚠️ ${d.issues.length} vulnérabilité${d.issues.length > 1 ? 's' : ''} détectée${d.issues.length > 1 ? 's' : ''}`}
                </span>
                {d.issues?.length > 0 && (
                  <span style={{ fontSize:'0.68rem', color:'#f87171', background:'rgba(248,113,113,0.1)', padding:'2px 8px', borderRadius:99 }}>
                    {d.issues.reduce((a, i) => a + Math.abs(i.impact), 0)} pts perdus
                  </span>
                )}
              </div>
              {d.issues?.length > 0 && (
                <span style={{ fontSize:'0.7rem', color:'#555' }}>Chaque correction a un bouton copier →</span>
              )}
            </div>

            {/* Aucune vulnérabilité */}
            {d.issues?.length === 0 && (
              <div style={{ padding:'20px 22px', color:'#4ade80', fontSize:'0.85rem', fontWeight:600 }}>
                Profil Google Business complet — tous les critères sont validés.
              </div>
            )}

            {/* Liste des vulnérabilités */}
            {d.issues?.length > 0 && d.issues.map((issue, i) => {
              const templateMap = {
                'Aucun avis Google':  { id:'rv0',    text: COPY_TEMPLATES.review_request(d.name),             label:'Copier message client'   },
                'avis seulement':     { id:`rv${i}`, text: COPY_TEMPLATES.review_request(d.name),             label:'Copier message client'   },
                'avis':               { id:`rv${i}`, text: COPY_TEMPLATES.review_request(d.name),             label:'Copier message client'   },
                'Aucune photo':       { id:'ph0',    text: COPY_TEMPLATES.photos_checklist(),                  label:'Copier checklist photos'  },
                'photo(s) seulement': { id:`ph${i}`, text: COPY_TEMPLATES.photos_checklist(),                  label:'Copier checklist photos'  },
                'photos':             { id:`ph${i}`, text: COPY_TEMPLATES.photos_checklist(),                  label:'Copier checklist photos'  },
                'Pas de description': { id:'desc',   text: COPY_TEMPLATES.description_template(d.name, city), label:'Copier modèle description' },
                "Pas d'horaires":     { id:'hrs',    text: COPY_TEMPLATES.hours_reminder(),                    label:'Copier guide horaires'    },
              };
              const tplKey  = Object.keys(templateMap).find(k => issue.label.includes(k));
              const tpl     = tplKey ? templateMap[tplKey] : null;
              const fixLoc  = getFixLocation(issue.label, d.placeId);

              return (
                <div key={i} style={{ padding:'16px 22px', borderBottom: i < d.issues.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none', display:'flex', alignItems:'flex-start', gap:14 }}>
                  {/* Impact badge */}
                  <div style={{ flexShrink:0, width:44, height:44, borderRadius:10, background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.2)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', marginTop:2 }}>
                    <span style={{ color:'#f87171', fontSize:'0.75rem', fontWeight:900, lineHeight:1 }}>{issue.impact}</span>
                    <span style={{ color:'#f87171aa', fontSize:'0.55rem', fontWeight:700 }}>pts</span>
                  </div>

                  {/* Contenu */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
                      <span style={{ color:'#EAEAEA', fontSize:'0.84rem', fontWeight:700 }}>{issue.label}</span>
                    </div>
                    <div style={{ color:'#555', fontSize:'0.78rem', lineHeight:1.7 }}>
                      → {issue.action}
                    </div>

                    {/* Chemin visuel + lien direct */}
                    <FixPath location={fixLoc} placeId={d.placeId} copied={copied} onCopy={copy} />

                    {/* Bouton copier template */}
                    {tpl && (
                      <div style={{ marginTop:8 }}>
                        <CopyBtn text={tpl.text} id={tpl.id} label={tpl.label} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Grille info + points forts ── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>

            {/* Info fiche */}
            <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'20px 22px' }}>
              <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>Informations fiche</div>
              <InfoRow label="Téléphone"   value={d.phone} accent={d.phone ? '#888' : '#f87171'} />
              <InfoRow label="Site web"    value={d.website} accent={safeUrl(d.website) ? '#4db6ff' : d.website ? '#fbbf24' : '#f87171'} />
              <InfoRow label="Note"        value={d.rating != null ? `${d.rating}/5 ⭐` : null} accent={d.rating >= 4 ? '#4ade80' : d.rating >= 3.5 ? '#fbbf24' : '#f87171'} />
              <InfoRow label="Avis"        value={`${d.reviews} avis`} accent={d.reviews >= 30 ? '#4ade80' : d.reviews >= 10 ? '#fbbf24' : '#f87171'} />
              <InfoRow label="Photos"      value={`${d.photos} photos`} accent={d.photos >= 15 ? '#4ade80' : d.photos >= 5 ? '#fbbf24' : '#f87171'} />
              <InfoRow label="Horaires"    value={d.hasHours ? 'Renseignés ✓' : 'Manquants'} accent={d.hasHours ? '#4ade80' : '#f87171'} />
              <InfoRow label="Description" value={d.hasDescription ? 'Présente ✓' : 'Manquante'} accent={d.hasDescription ? '#4ade80' : '#f87171'} />
              {d.hasHours && d.hours && (
                <div style={{ marginTop:8 }}>
                  <button onClick={() => setOpenHours(v => !v)}
                    style={{ background:'transparent', border:'none', color:'#444', fontSize:'0.7rem', cursor:'pointer', padding:0, fontWeight:600 }}>
                    {openHours ? '▲ Masquer les horaires' : '▼ Voir les horaires'}
                  </button>
                  {openHours && (
                    <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:3 }}>
                      {d.hours.map((h, i) => <div key={i} style={{ color:'#555', fontSize:'0.72rem' }}>{h}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Points forts + IA */}
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {d.strengths?.length > 0 && (
                <div style={{ background:'rgba(74,222,128,0.04)', border:'1px solid rgba(74,222,128,0.15)', borderRadius:14, padding:'20px 22px', flex:1 }}>
                  <div style={{ fontSize:'0.65rem', color:'#4ade8088', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>Points forts</div>
                  {d.strengths.map((s, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:9, padding:'7px 0', borderBottom: i < d.strengths.length-1 ? '1px solid rgba(74,222,128,0.07)' : 'none' }}>
                      <span style={{ color:'#4ade80', fontSize:'0.85rem', flexShrink:0 }}>✓</span>
                      <span style={{ color:'#4ade80cc', fontSize:'0.8rem' }}>{s}</span>
                    </div>
                  ))}
                </div>
              )}

              {d.aiRecommendations?.length > 0 && (
                <div style={{ background:'#080810', border:'1px solid rgba(139,92,246,0.2)', borderRadius:14, padding:'20px 22px' }}>
                  <div style={{ fontSize:'0.65rem', color:'#a78bfa88', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>✨ Recommandations IA</div>
                  {d.aiRecommendations.map((r, i) => (
                    <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'7px 0', borderBottom: i < d.aiRecommendations.length-1 ? '1px solid rgba(139,92,246,0.07)' : 'none' }}>
                      <span style={{ color:'#a78bfa', fontWeight:900, fontSize:'0.8rem', flexShrink:0 }}>{i+1}.</span>
                      <span style={{ color:'#888', fontSize:'0.8rem', lineHeight:1.5 }}>{r}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Boîte à outils ── */}
          <div style={{ background:'#0a0a0d', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, overflow:'hidden' }}>
            <div style={{ padding:'14px 22px', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ color:'#555', fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase' }}>
                📋 Boîte à outils — textes prêts à copier
              </span>
            </div>

            {/* Lien avis direct (si placeId disponible) */}
            {d.placeId && (
              <div style={{ padding:'14px 22px', borderBottom:'1px solid rgba(255,255,255,0.04)', background:'rgba(251,191,36,0.04)' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, flexWrap:'wrap', gap:8 }}>
                  <span style={{ color:'#fbbf24', fontSize:'0.8rem', fontWeight:700 }}>⭐ Lien direct — formulaire avis Google</span>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => copy(`https://search.google.com/local/writereview?placeid=${d.placeId}`, 'direct_review')}
                      style={{ background: copied === 'direct_review' ? 'rgba(74,222,128,0.1)' : 'transparent', border:`1px solid ${copied === 'direct_review' ? 'rgba(74,222,128,0.3)' : 'rgba(251,191,36,0.25)'}`, color: copied === 'direct_review' ? '#4ade80' : '#fbbf24', borderRadius:7, padding:'5px 12px', cursor:'pointer', fontSize:'0.72rem', fontWeight:700 }}>
                      {copied === 'direct_review' ? '✓ Copié' : 'Copier le lien'}
                    </button>
                    <a href={`https://search.google.com/local/writereview?placeid=${d.placeId}`} target="_blank" rel="noopener noreferrer"
                      style={{ background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.25)', color:'#fbbf24', borderRadius:7, padding:'5px 12px', fontSize:'0.72rem', fontWeight:700, textDecoration:'none' }}>
                      Tester ↗
                    </a>
                  </div>
                </div>
                <code style={{ fontSize:'0.68rem', color:'#555', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:6, padding:'5px 10px', display:'block', wordBreak:'break-all' }}>
                  {`https://search.google.com/local/writereview?placeid=${d.placeId}`}
                </code>
                <div style={{ color:'#444', fontSize:'0.68rem', marginTop:6 }}>
                  Envoyez ce lien à vos clients par SMS, email ou QR code — il les emmène directement sur le formulaire d'avis Google.
                </div>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
              {[
                { id:'t_review', label:"Message demande d'avis", icon:'⭐', text: COPY_TEMPLATES.review_request(d.name) },
                { id:'t_photos', label:'Checklist photos',        icon:'📸', text: COPY_TEMPLATES.photos_checklist() },
                { id:'t_desc',   label:'Modèle de description',   icon:'📝', text: COPY_TEMPLATES.description_template(d.name, city) },
                { id:'t_hours',  label:'Guide horaires',          icon:'⏰', text: COPY_TEMPLATES.hours_reminder() },
              ].map((item, i) => (
                <div key={item.id} style={{ padding:'14px 20px', borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none', borderRight: i % 2 === 0 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ color:'#888', fontSize:'0.8rem', fontWeight:600 }}>{item.icon} {item.label}</span>
                    <CopyBtn text={item.text} id={item.id} />
                  </div>
                  <pre style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.05)', borderRadius:7, padding:'8px 10px', fontSize:'0.67rem', color:'#333', margin:0, whiteSpace:'pre-wrap', fontFamily:'monospace', lineHeight:1.6, maxHeight:76, overflow:'hidden' }}>
                    {item.text.slice(0, 120)}...
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      </>}

    </div>
  );
}
